
import { Component, OnDestroy, OnInit, SimpleChanges, ChangeDetectorRef, ElementRef, ChangeDetectionStrategy, inject, input, linkedSignal, output, viewChild } from "@angular/core";
import {CartItem, CartService, CartStructure} from "app/Cart/CartService";
import {Subscription, throwError} from "rxjs";
import { QdmService } from "app/Services/qdm.service";
import { FormsModule } from "@angular/forms";
import { AdmissionService } from "app/Services/admission.service";
import { FullAdmissionsData } from "app/Services/gestion.service";
import { ToastrService } from "ngx-toastr";
@Component({
    imports: [FormsModule],
    selector: 'add-to-cart-button',
    template: `
  <div class="w-full h-full relative overflow-hidden rounded flex-1 select-none bg-vert-3 fg-vert-1 px-1">
    <section
      (click)="this.addToCart(); this.preventModalOpen($event)"
      [class.animate-swipe-from-right]="this.quantityPresentInCart === 0"
      [class.animate-swipe-left]="this.quantityPresentInCart > 0"
      class="absolute inset-0 w-full h-full flex items-center justify-center gap-1 cursor-pointer hover:bg-vert-1 hover:text-white transition-all">
      <span [innerText]="this.baseLabel()" class="fadeIn opacity-0 font-bold text-xs md:text-sm text-center block my-auto"></span>
      <span [innerText]="this.baseIcon()" class="fadeIn opacity-0 text-md material-icons"></span>
    </section>
    <section
      [class.animate-swipe-from-right]="this.quantityPresentInCart > 0"
      [class.animate-swipe-left]="this.quantityPresentInCart === 0"
      class="absolute inset-0 w-full h-full flex items-center justify-center gap-1 bg-vert-3">
      <button
        (click)="this.incrementQuantity(-1); this.preventModalOpen($event)"
        class="cursor-pointer hover:bg-vert-1 hover:fg-vert-3 transition-all fadeIn opacity-0 flex items-center justify-center flex-1 h-full fg-vert-1">
        <span class="material-icons">remove</span>
      </button>
      <div class="flex items-center justify-center flex-1 h-full fg-vert-1" (click)="startEditing(); this.preventModalOpen($event)"
        >
        @if (!isEditing) {
          <span
            class="fadeIn opacity-0 text-lg font-extrabold cursor-pointer">
            <span [class.animate-[bounce_300ms_ease_1]]="this.animate">
              {{ this.quantityPresentInCart }}
            </span>
          </span>
        }
        @if (isEditing) {
          <input
            #quantityInput
            type="number"
            [(ngModel)]="tempQuantity"
            (blur)="finishEditing()"
            (keyup.enter)="finishEditing()"
            class="w-12 text-center bg-transparent border-b-2 border-vert-1 focus:outline-none"
            min="0"
            [max]="remainingQuantity()"
            autofocus>
        }
      </div>
      <button
        (click)="this.incrementQuantity(1); this.preventModalOpen($event)"
        class="cursor-pointer hover:bg-vert-1 hover:fg-vert-3 transition-all fadeIn opacity-0 flex items-center justify-center flex-1 h-full fg-vert-1">
        <span class="material-icons">add</span>
      </button>
    </section>
  </div>
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./AddToCartButton.component.scss']
})
export class AddToCartButtonComponent implements OnInit, OnDestroy {
  private cart = inject(CartService);
  private cdr = inject(ChangeDetectorRef);
  private qdm = inject(QdmService);
  private admissionService = inject(AdmissionService);
  private toast = inject(ToastrService);

  animate = false;
  private sub: Subscription = new Subscription();
  isEditing = false;
  tempQuantity = 0;

  readonly baseIcon = input<string>("add");
  readonly baseLabel = input<string>("Commander");

  /**
   * Si la quantité >= 1, on affiche les boutons de quantité
   * Si la quantité === 0, on affiche le bouton d'ajout
   */
  quantityPresentInCart: number = 0;
  readonly cartItem = input.required<CartItem | undefined>();
  readonly serviceId = input.required<number>();
  /** Valeur effective : initialisée par l'input, surchargée par le state global */
  protected readonly serviceIdState = linkedSignal(() => this.serviceId());
  readonly restaurantId = input.required<number>();
  readonly childId = input.required<number>();
  /** Valeur effective : initialisée par l'input, surchargée par le state global */
  protected readonly childIdState = linkedSignal(() => this.childId());
  readonly remainingQuantity = input.required<number>();

  readonly onQuantityChanged = output<{
    "cartItem": CartItem;
    "qty": number;
}>();
  date: any;
  cartContent: CartStructure[] = [];
  currentServiceId: number = -1;
  currentChildId: number = -1;
  selectedAdmission: FullAdmissionsData | null = null;
  groupOrderQuantity: number = 1;

  readonly quantityInput = viewChild.required<ElementRef<HTMLInputElement>>('quantityInput');

  ngOnInit() {
    this.sub.add(this.qdm.selectedDate$.subscribe((date) => {
      this.date = date;
      this.cdr.detectChanges();
      this.updateCart();
    }));

    this.sub.add(this.cart.cart$.subscribe((cart) => {
      this.cartContent = cart;
      this.cdr.detectChanges();
      this.updateCart();
    }));

    this.sub.add(this.qdm.currentService$.subscribe(service => {
      if (service) {
        this.currentServiceId = service.id;
        // Mise à jour du serviceId si nécessaire
        if (this.serviceIdState() !== this.currentServiceId) {
          this.serviceIdState.set(this.currentServiceId);
        }
        this.updateCart();
      }
    }));

    this.sub.add(this.qdm.selectChild$.subscribe(child => {
      if (child) {
        this.currentChildId = child.id;
        // Mise à jour du childId si nécessaire
        if (this.childIdState() !== this.currentChildId) {
          this.childIdState.set(this.currentChildId);
        }
        this.updateCart();
      }
    }));
    
    this.sub.add(this.qdm.selectedAdmission$.subscribe(admission => {
      this.selectedAdmission = admission;
      this.cdr.detectChanges();
    }));
    
    this.sub.add(this.qdm.groupOrderQuantity$.subscribe(qty => {
      this.groupOrderQuantity = qty;
      this.cdr.detectChanges();
    }));
  }

  updateCart() {
    // Réinitialiser la quantité par défaut
    this.quantityPresentInCart = 0;
    
    // Vérifier que date et cartItem sont définis
    if (!this.date || !this.cartItem()) {
      return;
    }
    
    const dateFormatted = this.date.formatDate("forApi");
    const filteredCart = this.cartContent.find((c) => 
      c.childId === this.childIdState() && 
      c.date === dateFormatted &&
      c.serviceId === this.serviceIdState()
    );
    
    if (filteredCart) {
      const cartItemFound = filteredCart.items.find((i) => i.id === this.cartItem()?.id);
      if (cartItemFound) {
        this.quantityPresentInCart = cartItemFound.quantity;
        this.triggerAnimation();
      }
    }
    
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Si childId ou serviceId ou cartItem changent, mettre à jour le panier
    if (changes['childId'] || changes['serviceId'] || changes['cartItem']) {
      this.updateCart();
    }
    
    if (changes['quantityPresentInCart']) {
      this.triggerAnimation();
    }
  }

  triggerAnimation() {
    if (this.animate) return;
    this.animate = true;

    setTimeout(() => {
      this.animate = false;
    }, 300);
  }

  launchError(errorMessage: string) {
    this.toast.error(errorMessage);
    throwError(() => new Error(errorMessage))
  }

  // Vérifie si on a assez de slots légumes pour les plats complets
  private checkAvailableSlotsForVeganFullCourse(): boolean {
    const cartItem = this.cartItem();
    if (!cartItem || !this.selectedAdmission || !cartItem.isVeganFullCourse) {
      return true; // Si ce n'est pas un plat complet légume, on passe
    }

    // Pour un plat complet légume, il faut 2 slots légume
    const legumeComponent = this.selectedAdmission.admissionParts.find(part => part.isLegume);
    if (!legumeComponent) return true; // Pas de composante légume dans cette admission

    // On utilise le service d'admission pour vérifier la quantité restante
    const remainingLegumeQty = this.admissionService.getRemainingQuantity(
      this.serviceIdState(),
      legumeComponent.serviceId,
      this.restaurantId(),
      this.childIdState(),
      this.date,
      this.groupOrderQuantity,
      this.selectedAdmission
    );

    console.log("remainingLegumeQty", remainingLegumeQty)

    // Pour un plat complet légume, il faut 2 slots légume
    return remainingLegumeQty >= 2;
  }

  // Vérifie si on a assez de slots légumes et viandes pour les plats complets viande
  private checkAvailableSlotsForMeatFullCourse(): boolean {
    const cartItem = this.cartItem();
    if (!cartItem || !this.selectedAdmission || !cartItem.isMeatFullCourse) {
      return true; // Si ce n'est pas un plat complet viande, on passe
    }

    // Pour un plat complet viande, il faut 1 slot légume et 1 slot viande
    const legumeComponent = this.selectedAdmission.admissionParts.find(part => part.isLegume);
    const meatComponent = this.selectedAdmission.admissionParts.find(part => !part.isLegume && !part.serviceIsNotInAdmission);

    if (!legumeComponent || !meatComponent) return true; // Pas de composantes nécessaires dans cette admission

    // On vérifie les quantités restantes pour les deux composantes
    const remainingLegumeQty = this.admissionService.getRemainingQuantity(
      this.serviceIdState(),
      legumeComponent.serviceId,
      this.restaurantId(),
      this.childIdState(),
      this.date,
      this.groupOrderQuantity,
      this.selectedAdmission
    );

    const remainingMeatQty = this.admissionService.getRemainingQuantity(
      this.serviceIdState(),
      meatComponent.serviceId,
      this.restaurantId(),
      this.childIdState(),
      this.date,
      this.groupOrderQuantity,
      this.selectedAdmission
    );

    // Il faut au moins 1 slot légume et 1 slot viande
    return remainingLegumeQty >= 1 && remainingMeatQty >= 1;
  }

  addToCart() {
    const cartItem = this.cartItem();
    if (!cartItem) {
      return this.launchError("Impossible d'ajouter un article sans informations");
    }

    const restaurantId = this.restaurantId();
    if (restaurantId === -1) {
      return this.launchError("Impossible d'ajouter un article sans restaurant");
    }

    const serviceId = this.serviceIdState();
    if (serviceId === -1) {
      return this.launchError("Impossible d'ajouter un article sans service");
    }

    if (this.date === undefined) {
      return this.launchError("Impossible d'ajouter un article sans date");
    }
    
    // Vérifier s'il s'agit d'un plat complet et si on a assez de slots disponibles
    if (cartItem.isVeganFullCourse && !this.checkAvailableSlotsForVeganFullCourse()) {
      return this.launchError("Impossible d'ajouter ce plat complet légume : il faut 2 emplacements légume disponible.");
    }
    
    if (cartItem.isMeatFullCourse && !this.checkAvailableSlotsForMeatFullCourse()) {
      return this.launchError("Impossible d'ajouter ce plat complet viande : il faut 1 emplacement légume et 1 emplacement viande disponible.");
    }

    this.cart.addItemToCart(cartItem, serviceId, restaurantId, this.childIdState(), this.date);
    this.quantityPresentInCart = 1;

    this.onQuantityChanged.emit({qty: this.quantityPresentInCart, cartItem: cartItem});
  }

  preventModalOpen(event: MouseEvent) {
    event.stopPropagation();
  }

  incrementQuantity(quantity: number) {
    const cartItem = this.cartItem();
    if (!cartItem) {
      return this.launchError("Impossible de modifier la quantité sans article !");
    }

    if (!this.date) {
      return this.launchError("Impossible de modifier la quantité sans date !");
    }

    if (quantity > 0) {
      // Vérification des slots disponibles pour les plats complets
      if (cartItem.isVeganFullCourse && !this.checkAvailableSlotsForVeganFullCourse()) {
        return this.launchError("Impossible d'augmenter la quantité : il faut 2 slots légume disponibles");
      }
      
      if (cartItem.isMeatFullCourse && !this.checkAvailableSlotsForMeatFullCourse()) {
        return this.launchError("Impossible d'augmenter la quantité : il faut 1 slot légume et 1 slot viande disponibles");
      }
      
      if (this.remainingQuantity() === 0) {
        return;
      }
    }

    this.quantityPresentInCart += quantity;
    if (this.quantityPresentInCart <= 0) {
      this.cart.removeItemFromCart(cartItem.id, this.serviceIdState(), this.restaurantId(), this.childIdState(), this.date);
      this.onQuantityChanged.emit({qty: 0, cartItem: cartItem});
      return;
    }

    this.cart.setItemQuantity(cartItem.id, this.quantityPresentInCart, this.serviceIdState(), this.restaurantId(), this.childIdState(), this.date);

    this.onQuantityChanged.emit({qty: this.quantityPresentInCart, cartItem: cartItem});
  }

  startEditing() {
    this.isEditing = true;
    this.tempQuantity = this.quantityPresentInCart;
    this.cdr.detectChanges();

    setTimeout(() => {
      const quantityInput = this.quantityInput();
      if (quantityInput) {
        quantityInput.nativeElement.select();
      }
    }, 0);
  }

  finishEditing() {
    this.isEditing = false;
    
    // Vérification de la quantité minimale
    if (this.tempQuantity < 0) {
      this.tempQuantity = 0;
    }

    // Vérification de la quantité maximale en tenant compte de la quantité déjà présente
    const maxPossibleQuantity = this.remainingQuantity() + this.quantityPresentInCart;
    if (this.tempQuantity > maxPossibleQuantity) {
      this.tempQuantity = maxPossibleQuantity;
    }
    
    const difference = this.tempQuantity - this.quantityPresentInCart;
    if (difference !== 0) {
      // Vérification des slots disponibles pour les plats complets si on augmente la quantité
      if (difference > 0) {
        const cartItem = this.cartItem();
        if (cartItem?.isVeganFullCourse && !this.checkAvailableSlotsForVeganFullCourse()) {
          this.tempQuantity = this.quantityPresentInCart;
          this.cdr.detectChanges();
          return this.launchError("Impossible d'augmenter la quantité : il faut 2 slots légume disponibles");
        }
        
        if (cartItem?.isMeatFullCourse && !this.checkAvailableSlotsForMeatFullCourse()) {
          this.tempQuantity = this.quantityPresentInCart;
          this.cdr.detectChanges();
          return this.launchError("Impossible d'augmenter la quantité : il faut 1 slot légume et 1 slot viande disponibles");
        }
      }
      
      this.incrementQuantity(difference);
    }
    
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}